import { useState } from 'react';
/**
 * Hook for lazy loading feedback types and submitting feedback
 * @param companyId - The company ID for API routing
 * @returns Feedback operations and state
 */
export const useFeedback = (companyId) => {
    const [feedbackTypes, setFeedbackTypes] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    /**
     * Lazy load feedback types from API (cached on server for 15 minutes)
     */
    const loadFeedbackTypes = async () => {
        // Don't reload if already loaded
        if (feedbackTypes) {
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            console.log('[useFeedback] Loading feedback types for company:', companyId);
            const response = await fetch(`/hub/${companyId}/api/ama/feedback-types`);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[useFeedback] Feedback types request failed:', response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            const data = await response.json();
            if (data.success !== false && data.thumbsOptions) {
                console.log('[useFeedback] Feedback types loaded successfully');
                setFeedbackTypes({
                    thumbsOptions: data.thumbsOptions,
                    reportOptions: data.reportOptions,
                });
            }
            else {
                console.error('[useFeedback] Invalid feedback types response:', data);
                setError(data.error || 'Failed to load feedback types');
            }
        }
        catch (err) {
            console.error('[useFeedback] Error loading feedback types:', err);
            setError(err instanceof Error ? err.message : 'Failed to load feedback types');
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Submit feedback for a conversation turn
     */
    const submitFeedback = async (payload) => {
        try {
            console.log('[useFeedback] Submitting feedback:', {
                ...payload,
                companyId,
            });
            const response = await fetch(`/hub/${companyId}/api/ama/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...payload,
                    companyId,
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[useFeedback] Feedback submission failed:', response.status, errorText);
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${errorText}`,
                };
            }
            const data = await response.json();
            if (data.success) {
                console.log('[useFeedback] Feedback submitted successfully');
                return { success: true };
            }
            else {
                console.error('[useFeedback] Feedback submission returned error:', data);
                return {
                    success: false,
                    error: data.error || 'Failed to submit feedback',
                };
            }
        }
        catch (err) {
            console.error('[useFeedback] Error submitting feedback:', err);
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Failed to submit feedback',
            };
        }
    };
    /**
     * Delete feedback for a conversation turn (called when user re-clicks an active thumb)
     */
    const deleteFeedback = async (payload) => {
        try {
            const response = await fetch(`/hub/${companyId}/api/ama/feedback`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...payload, companyId }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[useFeedback] Feedback deletion failed:', response.status, errorText);
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${errorText}`,
                };
            }
            const data = await response.json();
            if (data.success) {
                console.log('[useFeedback] Feedback deleted successfully');
                return { success: true };
            }
            return {
                success: false,
                error: data.error || 'Failed to delete feedback',
            };
        }
        catch (err) {
            console.error('[useFeedback] Error deleting feedback:', err);
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Failed to delete feedback',
            };
        }
    };
    return {
        feedbackTypes,
        loadFeedbackTypes,
        submitFeedback,
        deleteFeedback,
        isLoading,
        error,
    };
};
